import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import GoogleButton from "@/components/GoogleButton";

const SIDE_IMG = "https://images.pexels.com/photos/5530484/pexels-photo-5530484.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center px-4 py-16 sm:px-6 lg:px-16">
        <div className="w-full max-w-md">
          <Kicker>Customer Login</Kicker>
          <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-4xl sm:text-5xl">
              Welcome
            </MaskedLine>
            <MaskedLine delay={0.22} className="text-4xl text-blue-900 sm:text-5xl">
              back.
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Sign in to NUC Service — National Union Corporation — to book services and track
              your bookings and payments.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-8">
              <GoogleButton testId="google-signin-button" />
            </div>
            <div className="my-6 flex items-center gap-4" aria-hidden="true">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                or with email
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <label htmlFor="login-email" className="field-label">Email</label>
                <input
                  id="login-email"
                  type="email"
                  data-testid="login-email-input"
                  className="field-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="login-password" className="field-label">Password</label>
                <input
                  id="login-password"
                  type="password"
                  data-testid="login-password-input"
                  className="field-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <p
                  data-testid="login-error-message"
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Signing in…" : "Sign in"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <p className="text-sm text-slate-600">
                No account yet?{" "}
                <Link
                  to="/register"
                  data-testid="login-register-link"
                  className="font-medium text-blue-900 hover:underline"
                >
                  Register free
                </Link>
              </p>
            </form>
          </Reveal>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img
          src={SIDE_IMG}
          alt="Citizen receiving digital assistance at a NUC Service desk"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-10 left-10 right-10">
          <p className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Bookings, payments and notices — one login.
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/70">
            NUC Service — National Union Corporation
          </p>
        </div>
      </div>
    </div>
  );
}
